/**
 * Role -> dashboard configuration.
 *
 * Deliberately data-only (no fetching, no JSX) so the visual layout
 * (DashboardSummary/PrimaryInsight/RecentActivity) stays identical across
 * roles and only the labels/icons/routes/metrics vary - avoids duplicating
 * entire dashboard layouts per role, per the redesign brief.
 *
 * Every metric here maps to a field that a REAL existing endpoint already
 * returns (see the field-by-field citations below) - nothing is invented.
 */

// Shared by AO / MRO / IMO / Technician / extTechnician - they all currently
// hit the SAME endpoint (GET /v2/activities/dashboard/sr-wo, verified in
// frontend/src/_metronic/_partials/dashboards/UserDashboard.js), which does
// not differentiate its response by role - only by data ownership/receiver
// scoping server-side. So the metric SET is necessarily the same for all of
// these roles today; only which ones are shown is refined via the existing
// permission checks (viewAllSRReport-style permissions aren't required here
// since these counts have no dedicated permission of their own - the
// underlying endpoint itself is the only gate).
// Each SR/WO count is computed server-side with the same filter as the list
// page it links to, and is null when the user can't open that list - those
// tiles are hidden rather than shown with a number the user can't verify.
const OPERATIONAL_METRICS = dashboardInfo =>
  [
    {
      key: "incomingSr",
      icon: "/media/svg/icons/Communication/Clipboard-list.svg",
      label: "Incoming service requests",
      value: dashboardInfo?.incomingSr,
      route: "/service-request/incoming"
    },
    {
      key: "outgoingSr",
      icon: "/media/svg/icons/Communication/Clipboard-list.svg",
      label: "Outgoing service requests",
      value: dashboardInfo?.outgoingSr,
      route: "/service-request/outgoing"
    },
    {
      key: "incomingWo",
      icon: "/media/svg/icons/Tools/Tools.svg",
      label: "Incoming work orders",
      value: dashboardInfo?.incomingWo,
      route: "/work-order/incoming"
    },
    {
      key: "outgoingWo",
      icon: "/media/svg/icons/Tools/Tools.svg",
      label: "Outgoing work orders",
      value: dashboardInfo?.outgoingWo,
      route: "/work-order/outgoing"
    },
    {
      key: "completedWo",
      icon: "/media/svg/icons/Communication/Clipboard-check.svg",
      label: "Completed work orders",
      caption: "Awaiting certification",
      value: dashboardInfo?.completedWo,
      route: "/work-order/completed"
    },
    {
      key: "certifiedWo",
      icon: "/media/svg/icons/Communication/Clipboard-check.svg",
      label: "Certified work orders",
      value: dashboardInfo?.certifiedWo,
      route: "/work-order/certified"
    },
    {
      key: "openedFaults",
      icon: "/media/svg/icons/Code/Warning-2.svg",
      label: "Open fault reports",
      value: dashboardInfo?.openedFaults || 0,
      route: "/asset/reported-faults",
      attention: (dashboardInfo?.openedFaults || 0) > 0
    }
  ].filter(metric => metric.value !== null);

export function getRoleDashboardConfig(userdata) {
  if (!userdata) return null;

  if (userdata.super) {
    return {
      role: "super",
      title: "Dashboard",
      subtitle: "Platform-wide overview across all companies.",
      endpoint: "/v2/dashboard-sa",
      buildMetrics: dashboardInfo => [
        {
          key: "ao",
          icon: "/media/svg/icons/Communication/Group.svg",
          label: "Asset owners",
          value: dashboardInfo?.ao,
          route: "/company/manage"
        },
        {
          key: "mro",
          icon: "/media/svg/icons/Communication/Group.svg",
          label: "MROs",
          value: dashboardInfo?.mro
        },
        {
          key: "technician",
          icon: "/media/svg/icons/Communication/Group.svg",
          label: "Technicians",
          value: dashboardInfo?.technician
        },
        {
          key: "supplier",
          icon: "/media/svg/icons/Communication/Group.svg",
          label: "Suppliers",
          value: dashboardInfo?.supplier
        }
      ],
      chart: dashboardInfo => ({
        title: "Platform registrations",
        subtitle: "Approved users registered per month.",
        categories: dashboardInfo?.categories,
        series: [{ name: "Registrations", data: dashboardInfo?.userData }]
      }),
      // Company count needs a second, existing, Super-Admin-only endpoint
      // (GET /v2/company - verified in src/controller/company.js) since
      // /v2/dashboard-sa doesn't return it. Real data, one extra request.
      extraMetric: {
        key: "companies",
        icon: "/media/svg/icons/Home/Building.svg",
        label: "Companies",
        route: "/company/manage"
      }
    };
  }

  if (userdata.supplier) {
    return {
      role: "supplier",
      title: "Dashboard",
      subtitle: "A quick overview of your purchase order activity.",
      endpoint: "/v2/dashboard-supplier",
      buildMetrics: dashboardInfo => [
        {
          key: "openedPO",
          icon: "/media/svg/icons/Shopping/Box2.svg",
          label: "Purchase orders awaiting response",
          value: dashboardInfo?.openedPO,
          route: "/purchase-order/incoming"
        },
        {
          key: "closedPO",
          icon: "/media/svg/icons/Communication/Clipboard-check.svg",
          label: "Completed purchase orders",
          value: dashboardInfo?.closedPO,
          route: "/purchase-order/completed"
        }
      ],
      chart: dashboardInfo => ({
        title: "Purchase order activity",
        subtitle: "Purchase orders received per month.",
        categories: dashboardInfo?.categories,
        series: [{ name: "Purchase orders", data: dashboardInfo?.poData, color: "#ff906d" }]
      })
    };
  }

  // AO / MRO / IMO / Technician / extTechnician (and any other role - this
  // mirrors the old Demo1Dashboard.js's final `: <UserDashboard />` catch-all).
  return {
    role: "operations",
    title: "Dashboard",
    subtitle: "A quick overview of your activity and operations.",
    endpoint: "/v2/activities/dashboard/sr-wo",
    buildMetrics: OPERATIONAL_METRICS,
    chart: dashboardInfo => ({
      title: "Maintenance activity",
      subtitle: "Service requests and work orders, per month.",
      categories: dashboardInfo?.categories,
      series: [
        { name: "Service requests", data: dashboardInfo?.srData, color: "#5aa7f5" },
        { name: "Work orders", data: dashboardInfo?.woData, color: "#ff906d" }
      ]
    }),
    // Asset condition and provider-performance are only meaningful (and only
    // permitted) for users holding the relevant report permissions - gated
    // at render time via the existing `Can`/permission-array check, not by
    // role flag, so this correctly covers AO admins as well as any MRO/IMO
    // staff who happen to hold the same permission.
    showAssetCondition: true,
    showProviderPerformance: true
  };
}
